import React, { CSSProperties, useMemo } from 'react';
import { useFormikContext } from 'formik';

import FormikSliderInput, { FormikSliderInputProps } from 'components/forms/formik/FormikSliderInput/FormikSliderInput';
import FormikFieldError from 'components/forms/formik/FormikFieldError/FormikFieldError';
import FormikNumberFormatInput from 'components/forms/formik/FormikNumberFormatInput/FormikNumberFormatInput';

import { BidSheetRow } from '../../../../hooks/useBidSheetState';

import './ElementEntry.scss';

export interface ElementEntryProps {
  name: 'volume' | 'bidPrice' | 'downPayment' | 'discount';
  label: string;
  hint?: string;
  sliderHint?: string;
  bid: BidSheetRow;
  prefix?: string;
  suffix?: string;
  decimalScale?: number;
  valueFormatter: (value: number | null | undefined) => string;
}

const ElementEntry = ({
  name,
  label,
  hint,
  bid,
  prefix,
  suffix,
  decimalScale = 0,
  sliderHint,
  valueFormatter,
}: ElementEntryProps) => {
  const { steps, getRangeColor, getTransformers } = bid[`${name}Config`];
  const [marks, min, max] = useMemo(() => {
    if (!steps) {
      return [undefined, undefined, undefined] as const;
    }

    return [
      steps.reduce(
        (acc, step) => ({
          ...acc,
          [step]: valueFormatter(step),
        }),
        {} as FormikSliderInputProps['marks'],
      ),
      steps[0],
      steps[steps.length - 1],
    ];
  }, [steps, valueFormatter]);

  const { getFieldMeta } = useFormikContext<BidSheetRow>();
  const { value } = getFieldMeta<number>(name);

  const rangeColor = getRangeColor(value) ?? 'transparent';
  const [transformIn, transformOut, step] = useMemo(
    () => getTransformers(min, max, decimalScale),
    [getTransformers, min, max, decimalScale],
  );

  return (
    <div
      className="enkrateia-element-entry"
      style={
        {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          '--range-color': rangeColor,
        } as CSSProperties
      }
    >
      <FormikNumberFormatInput
        name={name}
        label={label}
        variant="filled"
        prefix={prefix}
        suffix={suffix}
        decimalScale={decimalScale}
        fixedDecimalScale
        thousandSeparator
        hint={hint}
        hideErrors
      />
      {steps !== null && (
        <FormikSliderInput
          key={name}
          name={name}
          marks={marks}
          min={min}
          max={max}
          styles={{
            track: {
              backgroundColor: getRangeColor(value),
            },
          }}
          transformIn={transformIn}
          transformOut={transformOut}
          transformTooltip={valueFormatter}
          step={step ?? Math.pow(10, -decimalScale)}
        />
      )}
      {steps !== null && sliderHint && <div className="slider-hint">{sliderHint}</div>}
      <FormikFieldError name={name} />
    </div>
  );
};

export default ElementEntry;
